import * as React from "react"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  variant?: string;
  size?: string;
  asChild?: boolean;
  children?: React.ReactNode;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

// Simple slot component to render either a button or another component
const Slot: React.FC<{
  children?: React.ReactNode;
  [key: string]: any;
}> = ({ children, ...props }) => {
  // If a single child is provided, clone it with the props
  if (React.Children.count(children) === 1 && React.isValidElement(children)) {
    return React.cloneElement(children, { ...props });
  }
  
  return <>{children}</>;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "default", asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    // Apply styles based on variant and size
    const baseClass = "button";
    const variantClass = variant ? `${baseClass}--${variant}` : "";
    const sizeClass = size && size !== "default" ? `${baseClass}--${size}` : "";
    
    // Combine classes
    const buttonClass = [
      baseClass,
      variantClass,
      sizeClass,
      className || ""
    ].filter(Boolean).join(" ");
    
    return (
      <Comp
        className={buttonClass}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button }
